import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Paper,
  Table,
  TextInput,
  ActionIcon,
  ScrollArea,
  Tabs,
  Checkbox,
  Badge,
  Tooltip,
  UnstyledButton,
  Button,
  Group,
  HoverCard,
  Pagination,
  Stack,
  Skeleton,
  useMantineTheme,
} from '@mantine/core';
import {
  IconSearch,
  IconSettings,
  IconX,
  IconGripVertical,
  IconClock,
  IconArrowUp,
  IconArrowDown,
  IconInfoCircle,
  IconRefresh,
} from '@tabler/icons-react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  AdNetworkLevel,
  GRADE_COLUMNS,
  NetworkGoalDef,
  NetworkGoalMetric,
  NetworkRow,
  sourceChipColor,
  Tone,
  toneColor,
} from './types';
import { GradeCellTd, JourneyIconButton, metricDisplayLabel, CAMPAIGN_HIDE_SX, CampaignHideToggle, campaignHideKey, HiddenRowsToggle, AdminHelpTip, REPORT_HELP_IDS, SectionHelp } from './shared';
import {
  ASSISTS_UNAVAILABLE_MESSAGE,
  ASSIST_SORT_FIELD,
  ASSISTED_METRIC,
  assistCellValue,
  canOpenAssistJourney,
  isAssistMetric,
  positionAssistMetrics,
  withAssistMetrics,
  visibleGoalMetrics,
  goalHasRevenueByName,
} from './assists';
import { useAssistLoader, AssistContext, AssistFetcher, parseAssistResponse } from './useAssistLoader';
import AggregatedReportsApi from '../../api/AggregatedReportsApi';
import { AdPreviewDrawer } from '../CreateReport/AdPreviewDrawer';
import { ReportVelocityDrawer } from '../CreateReport/ReportVelocityDrawer';
import { AggregateCustomerJourney, AggregateCustomerJourneyParams } from './AggregateCustomerJourney';
import { AggregateKeywordDrawer, AggregateKeywordParams } from './AggregateKeywordDrawer';
import { AggregateAdKeywordDrawer, AggregateAdKeywordParams } from './AggregateAdKeywordDrawer';
import { SortState } from './sorting';
import { TRAFFIC_FUNNEL_METRICS } from './SettingsDrawer';
import { useColorScheme } from "../../components/GeneralComponent/GeneralComponent";

/* The bulk endpoint, reduced to what the loader needs. An HTTP error, a body that is not JSON,
   or anything parseAssistResponse rejects is a failed request, so nothing from it is cached. */
const fetchAssistCells: AssistFetcher = (params) =>
  AggregatedReportsApi.getadassistmetricsbulk(params)
    .then((r: Response) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then(parseAssistResponse);

const AD_PREVIEW_SOURCES = new Set(['facebook', 'google']);

// Default traffic column order; user drag-drops override.
export const TRAFFIC_CANONICAL_ORDER = [
  'Uniques',
  'Visits',
  'Ad Spend',
  'Network Conversion',
  'Impressions',
  'CTR',
  'CPC',
  'CPM',
  'Grade',
  'Grade Reason',
];

// Pinned identity column widths; each left offset is the sum of the widths before it.
export const STICKY_WIDTHS: Record<string, number> = {
  checkbox: 40, source: 110, campaign: 240, adset: 265, ad: 295,
};

type AdNetworkTabProps = {
  /* Which breakdown is active. Only 'cust_type' changes behaviour in this component. */
  breakdownType?: 'none' | 'device' | 'hour' | 'cust_type';
  level: AdNetworkLevel;
  rows: NetworkRow[];
  goals: NetworkGoalDef[];
  eligible: boolean;
  emptyMessage?: string;
  loading?: boolean;
  openSettings: (context: string, scope: string) => void;
  selectedIds: string[];
  setSelectedIds: (updater: (prev: string[]) => string[]) => void;
  drillFilterLabel?: string;
  onClearDrillFilter?: () => void;
  onDrillDown?: (row: NetworkRow) => void;
  trafficMetrics: string[];
  trafficFunnelMetricsByGoal: Record<string, string[]>;
  trafficGoalsEnabled: Record<string, boolean>;
  search: string;
  onSearchChange: (value: string) => void;
  page: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  totalRecords: number;
  perPage: number;
  velocityContext?: {
    report_id: string;
    visiopt_campaign_id: string[];
    attribution_model: string;
    report_time: string;
    ad_account_id?: string[];
    attribution_label?: string;
    attribute_window_type?: string;
    lookback_days?: number;
  };
  /* The settings this tab's rows were loaded with. Assist figures are requested under these,
     never under unsaved header / drawer edits, so they always describe the rows on screen. */
  assistContext?: AssistContext | null;
  sort: SortState;
  onSortChange: (next: SortState) => void;
  // Downloads the report's complete keyword file; every Keyword View export runs this.
  onKeywordExport?: () => Promise<string>;
  // Session-wide hidden campaigns, shared with the other levels and the Funnel traffic breakdown.
  hiddenCampaignKeys: string[];
  onToggleHiddenCampaign: (key: string) => void;
  hiddenRows: NetworkRow[];
  /* Whether the chosen attribution model can produce Assisted / Influenced Revenue. False under
     Same Day and Full Impact, where the cells read "—" and the note below the header explains
     how to get numbers. Decided by the report, not here, so the rule is the same everywhere. */
  assistsSupported?: boolean;
  /* true when the payload already carries the figures; false means fetch per ad. */
  assistsLoaded?: boolean;
  assistsMessage?: string;
};

export const TRAFFIC_METRIC_SORT_FIELD: Record<string, string> = {
  'Grade': 'grade',
  'Grade Reason': 'grade_reason',
  'Uniques': 'uniques',
  'Visits': 'visits',
  'Ad Spend': 'spend',
  'CPC': 'cpc',
  'CPM': 'cpm',
  'CTR': 'ctr',
  'Impressions': 'impressions',
  'Network Conversion': 'network_conversions',
  'Network Conv.': 'network_conversions',
};
export const GOAL_METRIC_SORT_FIELD: Record<string, string> = {
  'Conversions': 'conversions',
  ...ASSIST_SORT_FIELD,
  'Revenue': 'revenue',
  'CPA': 'cpa',
  'ROAS': 'roas',
  'UCR': 'ucr',
  'CR': 'cr',
  'AOV': 'aov',
  'ACV': 'acv',
  'EPC': 'epc',
  'Unique EPC': 'arpu',
  'Profit': 'profit',
  'ROI': 'roi',
  'Goal Visits': 'goal_visits',
  /* Only Result sorts, and only on its numeric side. Scored has no
     entry on purpose - sorting by it alone is meaningless - which leaves its
     header button disabled. */
  'Avg. Value': 'avg_value',
};
const HEADER_LABEL_TO_FIELD: Record<string, string> = {
  'Campaign': 'campaign',
  'Ad Set':   'adset',
  'Ad':       'ad',
};

// Cycles asc → desc → asc on the same column; clicking a different column starts asc.
const cycleSort = (prev: SortState, field: string): SortState => {
  if (prev && prev.field === field) {
    return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { field, dir: 'asc' };
};

type Cell = { value: string; tone?: Tone | null; hint?: string };

// Map a backend row + traffic-metric label to a displayable cell value.
// Both old + new labels are recognised so saved views written before the rename
// still render the right cell. Only Spend / Uniques / Visits can carry a benchmark.
export const trafficCell = (row: NetworkRow, metric: string): Cell => {
  switch (metric) {
    case 'Visits':
      return { value: typeof row.visits === 'number' ? row.visits.toLocaleString() : '-', tone: row.visitsTone };
    case 'Uniques':
      return { value: typeof row.uniques === 'number' ? row.uniques.toLocaleString() : '-', tone: row.uniquesTone };
    case 'Ad Spend':
      return { value: row.spend ?? '-', tone: row.spendTone };
    case 'CPC':
      return { value: row.cpc ?? '-' };
    case 'CPM':
      return { value: row.cpm ?? '-' };
    case 'CTR':
      return { value: row.ctr ?? '-' };
    // null = not available for this row (Organic / Direct rows have no ad network); Device/Hour sub-rows carry none at all.
    case 'Impressions':
      return { value: row.impressions == null ? '-' : row.impressions.toLocaleString() };
    case 'Network Conversion':
    case 'Network Conv.':
      // Hover names the event the network counts; a campaign whose ad sets count different events has no single result.
      return {
        value: row.network_conversions == null ? '-' : Number(row.network_conversions).toLocaleString(),
        hint: row.network_conversion_event === 'mixed'
          ? 'Multiple conversion events. See the Ad Set tab for results.'
          : (row.network_conversion_event ? `Event: ${row.network_conversion_event}` : undefined),
      };
    default:
      return { value: '-' };
  }
};

// Tinted only when a benchmark matched, so untouched cells keep their normal weight.
export const cellBody = (c: Cell) => {
  const body = c.tone
    ? <span style={{ color: toneColor(c.tone), fontWeight: 600 }}>{c.value}</span>
    : c.value;
  return c.hint
    ? <Tooltip label={c.hint} multiline width={260} withArrow withinPortal><span>{body}</span></Tooltip>
    : body;
};

// Device and Hour rows split spend by each row's share of visits, so every spend-based cell there is an estimate.
export const ESTIMATED_BREAKDOWN_METRICS = ['Ad Spend', 'CPC', 'CPA', 'ROAS', 'Profit', 'ROI'];
export const ESTIMATED_SPEND_TOOLTIP = "Estimated. Ad networks don't report spend at this level of detail, so total spend is split by each row's share of visits.";
export const estimatedValue = (value: string) => (value && value !== '-' ? `~${value}` : value);
// CPC on these rows is the estimated spend over the row's unique visitors, not the network's cost per click.
export const ESTIMATED_CPC_TOOLTIP = 'Estimated spend only per unique visitor.';
export const estimatedCellBody = (c: Cell, metric?: string) =>
  c.value && c.value !== '-'
    ? (
      <Tooltip label={metric === 'CPC' ? ESTIMATED_CPC_TOOLTIP : ESTIMATED_SPEND_TOOLTIP} multiline width={280} withArrow withinPortal>
        <span>{cellBody({ ...c, value: estimatedValue(c.value) })}</span>
      </Tooltip>
    )
    : cellBody(c);

export const goalCell =(m: NetworkGoalMetric | undefined, metric: string): Cell => {
  if (isAssistMetric(metric)) { return { value: assistCellValue(metric, m) }; }
  if (!m) { return { value: '-' }; }
  switch (metric) {
    case 'Conversions': return { value: Number(m.conversions ?? 0).toLocaleString(), tone: m.conversionsTone };
    case 'Revenue':     return { value: m.revenue, tone: m.revenueTone };
    case 'Profit':      return { value: m.profit,  tone: m.profitTone };
    case 'UCR':         return { value: m.ucr,     tone: m.ucrTone };
    case 'CR':          return { value: m.cr,      tone: m.crTone };
    case 'CPA':         return { value: m.cpa,     tone: m.cpaTone };
    case 'ROAS':        return { value: m.roas,    tone: m.roasTone };
    case 'ROI':         return { value: m.roi,     tone: m.roiTone };
    case 'AOV':         return { value: m.aov,     tone: m.aovTone };
    case 'ACV':         return { value: m.acv ?? '-', tone: m.acvTone };
    case 'EPC':         return { value: m.epc,     tone: m.epcTone };
    // Avg. Value shows '-' rather than 0 when nothing scored: no score is not
    // a score of zero. Scored is a count, so 0 is a real answer there.
    case 'Avg. Value':  return { value: m.avg_value && m.avg_value !== '-' && m.score_max ? `${m.avg_value} / ${m.score_max}` : (m.avg_value ?? '-') };
    case 'Scored':      return { value: (m.scored ?? 0).toLocaleString() };
    case 'Unique EPC':
      return { value: m.arpu ?? '-', tone: m.arpuTone };
    default:            return { value: '-' };
  }
};

/* One Result cell covering every shape a goal can produce. Numbers show their
   average; words show the biggest label and its share, or "Mixed" on a tie. The
   full split is on hover, so merging the columns never hides values. */
const ResultCell = ({ metric }: { metric?: NetworkGoalMetric }) => {
  const labels = metric?.value_breakdown ?? [];
  const average = metric?.avg_value && metric.avg_value !== '-'
    ? (metric.score_max ? `${metric.avg_value} / ${metric.score_max}` : metric.avg_value)
    : '';
  // 40% rather than 40.00%; a decimal only when the share is not whole.
  const pctText = (pct: number | string) => `${Number(Number(pct).toFixed(1))}%`;

  if (!average && labels.length === 0) { return <>-</>; }
  // Numbers only: nothing to expand into.
  if (average && labels.length === 0) { return <>{average}</>; }

  const top = labels[0];
  // More than one label at the top share means there is no single leader.
  const tied = labels.filter((r) => r.count === top.count).length > 1;
  const summary = average || (tied ? 'Mixed' : `${top.label} ${pctText(top.pct)}`);

  return (
    <HoverCard position="bottom" withArrow shadow="md" withinPortal openDelay={100} closeDelay={100}>
      <HoverCard.Target>
        <Text component="span" size="sm" sx={{ whiteSpace: 'nowrap', cursor: 'default', borderBottom: '1px dotted currentColor' }}>
          {summary}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack spacing={4}>
          {average && (
            <Group position="apart" spacing="lg" noWrap>
              <Text size="xs" color="dimmed" sx={{ whiteSpace: 'nowrap' }}>Average</Text>
              <Text size="xs" weight={600}>{average}</Text>
            </Group>
          )}
          {labels.map((r) => (
            <Group key={r.label} position="apart" spacing="lg" noWrap>
              <Text size="xs" sx={{ whiteSpace: 'nowrap' }}>{r.label}</Text>
              <Text size="xs" weight={600} sx={{ whiteSpace: 'nowrap' }}>{pctText(r.pct)} ({r.count})</Text>
            </Group>
          ))}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

/* Result is one column carrying whatever the goal actually produced. Exported
   because FunnelTrafficBreakdown renders the same goal cells. */
export const renderGoalCell = (metric: NetworkGoalMetric | undefined, m: string) => {
  if (m === 'Avg. Value' || m === 'Value Breakdown') {
    return <ResultCell metric={metric} />;
  }
  return cellBody(goalCell(metric, m));
};

const LEVEL_CONFIG: Record<
  AdNetworkLevel,
  {
    tabValue: string;
    helper: string;
    settingsContext: string;
    settingsScope: string;
    searchPlaceholder: string;
    searchTitle: string;
    minWidth: number;
    extraColumnLabels: string[];
    defaultEmptyMessage: string;
  }
> = {
  campaign: {
    tabValue: 'campaign',
    helper: 'Click a campaign name to drill into its ad sets, or the route icon to see customer journeys.',
    settingsContext: 'Campaign Settings',
    settingsScope: 'Applies to Campaign · Ad Set · Ad',
    searchPlaceholder: 'Search campaigns...',
    searchTitle: 'Campaign Settings',
    minWidth: 1000,
    extraColumnLabels: ['Campaign'],
    defaultEmptyMessage: 'No campaigns match the current filters.',
  },
  adset: {
    tabValue: 'adset',
    helper: 'Click an ad set name to drill into its ads, or the route icon to see customer journeys.',
    settingsContext: 'Ad Set Settings',
    settingsScope: 'Applies to Ad Set · Ad',
    searchPlaceholder: 'Search ad sets...',
    searchTitle: 'Ad Set Settings',
    minWidth: 1100,
    extraColumnLabels: ['Campaign', 'Ad Set'],
    defaultEmptyMessage: 'No ad sets match the current filters.',
  },
  ad: {
    tabValue: 'ad',
    helper: 'Individual ad creatives and their performance. Click an ad name to view the ad. Click the route icon to see customer journeys.',
    settingsContext: 'Ad Settings',
    settingsScope: 'Applies to Ad',
    searchPlaceholder: 'Search ads...',
    searchTitle: 'Ad Settings',
    minWidth: 1200,
    extraColumnLabels: ['Campaign', 'Ad Set', 'Ad'],
    defaultEmptyMessage: 'No ads match the current filters.',
  },
};

// Keeps user drag order intact while merging new/removed entries from desired.
const syncOrderedList = (current: string[], desired: string[]): string[] => {
  const desiredSet = new Set(desired);
  const out = current.filter((m) => desiredSet.has(m));
  desired.forEach((m, i) => {
    if (out.includes(m)) { return; }
    const preceding = desired.slice(0, i).filter((x) => out.includes(x));
    const at = preceding.length === 0
      ? 0
      : out.indexOf(preceding[preceding.length - 1]) + 1;
    out.splice(at, 0, m);
  });
  return out;
};

// Column order for the traffic and goal groups, with drag-to-reorder inside each group. Shared so
// the Campaign / Ad Set / Ad tables and the Funnel tab's Traffic Breakdown order columns alike.
export const useMetricColumnOrder = (
  trafficMetrics: string[],
  trafficFunnelMetricsByGoal: Record<string, string[]>,
  activeGoals: NetworkGoalDef[],
  // Drops goal columns a table cannot show (assist columns off the Ad tab); callers without one keep every column.
  visibleForGoal: (goalName: string, metrics: string[]) => string[] = (_goalName, metrics) => metrics,
  visibilityKey = '',
) => {
  const canonicalTrafficMetrics = useMemo(() => {
    const idx = (m: string) => {
      const i = TRAFFIC_CANONICAL_ORDER.indexOf(m);
      if (i >= 0) { return i; }
      if (m === 'Network Conv.')   { return TRAFFIC_CANONICAL_ORDER.indexOf('Network Conversion'); }
      return TRAFFIC_CANONICAL_ORDER.length;
    };
    // Drop anything the canonical list no longer knows about, so a metric retired since the
    // view was saved (e.g. Placement, now a structural column) cannot resurface at the end.
    const known = trafficMetrics.filter((m) => idx(m) < TRAFFIC_CANONICAL_ORDER.length);
    return known.sort((a, b) => idx(a) - idx(b));
  }, [trafficMetrics]);

  // Frontend-only drag order; merged via syncOrderedList on drawer changes.
  const [trafficOrder, setTrafficOrder] = useState<string[]>(canonicalTrafficMetrics);
  const [goalOrder, setGoalOrder] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setTrafficOrder((prev) => {
      const next = syncOrderedList(prev, canonicalTrafficMetrics);
      const sameLen = next.length === prev.length;
      const sameOrder = sameLen && next.every((v, i) => v === prev[i]);
      return sameOrder ? prev : next;
    });
  }, [canonicalTrafficMetrics]);

  useEffect(() => {
    setGoalOrder((prev) => {
      const next: Record<string, string[]> = {};
      let changed = false;
      for (const goalName of Object.keys(trafficFunnelMetricsByGoal)) {
        /* Seed from the canonical goal order, not the order chips were toggled in, so the columns
           read exactly as the chooser lists them - assists included. */
        const picked = trafficFunnelMetricsByGoal[goalName] ?? [];
        const canonical = withAssistMetrics(TRAFFIC_FUNNEL_METRICS);
        const desired = visibleForGoal(goalName, [
          ...canonical.filter((m) => picked.includes(m)),
          ...picked.filter((m) => !canonical.includes(m)),
        ]);
        const current = prev[goalName] ?? [];
        /* Newly switched-on assist columns land right after Conversions instead of at the end
           of the goal group; everything else keeps whatever order the user dragged it into. */
        const synced = positionAssistMetrics(
          syncOrderedList(current, desired),
          desired.filter((m) => !current.includes(m))
        );
        next[goalName] = synced;
        if (
          !prev[goalName]
          || prev[goalName].length !== synced.length
          || prev[goalName].some((v, i) => v !== synced[i])
        ) {
          changed = true;
        }
      }
      for (const oldGoal of Object.keys(prev)) {
        if (!(oldGoal in trafficFunnelMetricsByGoal)) { changed = true; }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficFunnelMetricsByGoal, visibilityKey]);

  const orderedTrafficMetrics = trafficOrder;
  /* Average Value and Scored are one metric selection. Only
     'Avg. Value' is ever stored in the picker or the drag order; it expands to
     the pair here, so headers, cells and column counts stay in step and the two
     can never be shown, hidden or reordered apart. A standalone 'Scored' left in
     a saved view from before they were linked is dropped rather than duplicated. */
  const goalMetricsOrdered = (goalName: string): string[] => {
    const stored = goalOrder[goalName] ?? visibleForGoal(goalName, trafficFunnelMetricsByGoal[goalName] ?? []);
    const expanded: string[] = [];
    for (const metric of stored) {
      if (metric === 'Scored' || metric === 'Value Breakdown') { continue; }
      expanded.push(metric);
      if (metric === 'Avg. Value') { expanded.push('Scored'); }
    }
    return expanded;
  };

  // Cross-section drags are rejected so colSpan groupings stay aligned.
  const sectionRanges = useMemo(() => {
    const sections: Array<{ id: string; range: [number, number] }> = [];
    let cursor = 0;
    if (orderedTrafficMetrics.length > 0) {
      sections.push({ id: 'traffic', range: [cursor, cursor + orderedTrafficMetrics.length - 1] });
      cursor += orderedTrafficMetrics.length;
    }
    for (const goal of activeGoals) {
      const cols = goalMetricsOrdered(goal.name);
      if (cols.length === 0) { continue; }
      sections.push({ id: `goal:${goal.name}`, range: [cursor, cursor + cols.length - 1] });
      cursor += cols.length;
    }
    return sections;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderedTrafficMetrics.join('|'),
    activeGoals.map((g) => g.name).join('|'),
    activeGoals.map((g) => goalMetricsOrdered(g.name).join(',')).join('|'),
  ]);

  const sectionForIndex = (i: number): string | null => {
    for (const s of sectionRanges) {
      if (i >= s.range[0] && i <= s.range[1]) { return s.id; }
    }
    return null;
  };
  const sectionStartIndex = (id: string): number => {
    const hit = sectionRanges.find((s) => s.id === id);
    return hit ? hit.range[0] : 0;
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) { return; }
    const srcSection = sectionForIndex(source.index);
    const dstSection = sectionForIndex(destination.index);
    if (!srcSection || !dstSection || srcSection !== dstSection) { return; }
    const sectionStart = sectionStartIndex(srcSection);
    const localFrom = source.index - sectionStart;
    const localTo = destination.index - sectionStart;
    if (localFrom === localTo) { return; }
    if (srcSection === 'traffic') {
      setTrafficOrder((prev) => {
        const next = [...prev];
        const [moved] = next.splice(localFrom, 1);
        next.splice(localTo, 0, moved);
        return next;
      });
      return;
    }
    if (srcSection.startsWith('goal:')) {
      const goalName = srcSection.slice('goal:'.length);
      setGoalOrder((prev) => {
        /* The drag indices come from the rendered columns, which include the
           derived 'Scored' column, so the move has to happen in that same space
           and then collapse back to the single stored entry. Grabbing either
           half of the pair moves both, because only the anchor is stored. */
        const rendered = goalMetricsOrdered(goalName);
        const from = rendered[localFrom] === 'Scored' ? Math.max(0, localFrom - 1) : localFrom;
        const next = [...rendered];
        const [moved] = next.splice(from, 1);
        next.splice(localTo, 0, moved);
        return { ...prev, [goalName]: next.filter((m) => m !== 'Scored') };
      });
    }
  };

  return { orderedTrafficMetrics, goalMetricsOrdered, handleDragEnd };
};

export function AdNetworkTab({
  /* Which breakdown the report is showing. Only Customer Type changes anything here; the
     device and hour breakdowns keep driving the sub-rows exactly as before. */
  breakdownType = 'none',
  level,
  rows,
  goals,
  eligible,
  emptyMessage,
  loading,
  openSettings,
  selectedIds,
  setSelectedIds,
  drillFilterLabel,
  onClearDrillFilter,
  onDrillDown,
  trafficMetrics,
  trafficFunnelMetricsByGoal,
  trafficGoalsEnabled,
  search,
  onSearchChange,
  page,
  onPageChange,
  totalPages,
  totalRecords,
  perPage,
  velocityContext,
  assistContext,
  sort,
  onSortChange,
  onKeywordExport,
  hiddenCampaignKeys,
  onToggleHiddenCampaign,
  hiddenRows: serverHiddenRows,
  assistsSupported = false,
  assistsLoaded = false,
  assistsMessage = ASSISTS_UNAVAILABLE_MESSAGE,
}: AdNetworkTabProps) {

  const sortArrow = (field: string) =>
    sort?.field === field
      ? (sort.dir === 'asc' ? <IconArrowUp size="0.75rem" /> : <IconArrowDown size="0.75rem" />)
      : null;
  const handleSortToggle = (field: string) => onSortChange(cycleSort(sort, field));
  /* The chooser's per-goal selection is shared by Campaign / Ad Set / Ad, so the assist columns
     are filtered back out on the other two tabs, and Influenced Revenue is dropped for a goal
     that carries no money. */
  const goalHasRevenue = useMemo(() => goalHasRevenueByName(goals), [goals]);
  const visibleForGoal = (goalName: string, metrics: string[]): string[] =>
    visibleGoalMetrics(metrics, {
      isAdLevel: level === 'ad',
      goalHasRevenue: !!goalHasRevenue[goalName],
    });

  // New goals from the backend default to "on" until the user disables them.
  const activeGoals = goals.filter((g) => {
    const enabled = trafficGoalsEnabled[g.name];
    const enabledFlag = enabled === undefined ? true : enabled;
    /* Counted after filtering, so a goal whose only selected columns are assist columns is not
       treated as active on a tab that cannot show them - a header spanning zero columns. */
    const cols = visibleForGoal(g.name, trafficFunnelMetricsByGoal[g.name] ?? []);
    return enabledFlag && cols.length > 0;
  });

  // Column order and drag-to-reorder, shared with the Funnel tab's Traffic Breakdown.
  const { orderedTrafficMetrics, goalMetricsOrdered, handleDragEnd } = useMetricColumnOrder(
    trafficMetrics,
    trafficFunnelMetricsByGoal,
    activeGoals,
    visibleForGoal,
    `${level}|${Object.keys(goalHasRevenue).filter((k) => goalHasRevenue[k]).join(',')}`,
  );

  // Ad preview drawer state; only used at level === 'ad' for Facebook/Google rows.
  const [adPreviewOpen, setAdPreviewOpen] = useState(false);
  const [adPreview, setAdPreview] = useState<{ platform: 'facebook' | 'google'; adName: string; adId: string }>({
    platform: 'facebook',
    adName: '',
    adId: '',
  });

  // Keyword View drawer; ad-set level, Google rows only (an ad set is a Google ad group).
  const colorTheme = useColorScheme();
  const [keywordOpen, setKeywordOpen] = useState(false);
  const [keywordParams, setKeywordParams] = useState<AggregateKeywordParams | null>(null);
  const [adKeywordOpen, setAdKeywordOpen] = useState(false);
  const [adKeywordParams, setAdKeywordParams] = useState<AggregateAdKeywordParams | null>(null);
  const keywordViewButtonStyle = colorTheme === "dark"
    ? { fontSize: 10, padding: '2px 8px', border: '1px solid #91a7ff', borderRadius: 5, background: '#1f3a70', color: '#dbe4ff', cursor: 'pointer', whiteSpace: 'nowrap' }
    : { fontSize: 10, padding: '2px 8px', border: '1px solid #4c6ef5', borderRadius: 5, background: 'transparent', color: '#4c6ef5', cursor: 'pointer', whiteSpace: 'nowrap' };
  const keywordViewHoverInBg = colorTheme === "dark" ? '#2a4f96' : 'transparent';
  const keywordViewHoverOutBg = colorTheme === "dark" ? '#1f3a70' : 'transparent';
  const keywordViewActiveBg = colorTheme === "dark" ? '#19335f' : 'transparent';
  const [velocityOpen, setVelocityOpen] = useState(false);
  const [velocityParams, setVelocityParams] = useState<Record<string, any>>({});
  const [velocityDisplayName, setVelocityDisplayName] = useState('');

  const [journeyOpen, setJourneyOpen] = useState(false);
  const [journeyParams, setJourneyParams] = useState<AggregateCustomerJourneyParams | null>(null);
  const [journeyEntityLabel, setJourneyEntityLabel] = useState('');

  const goalColCount = activeGoals.reduce(
    (sum, g) => sum + goalMetricsOrdered(g.name).length,
    0
  );

  const theme = useMantineTheme();
  const goalDividerColor =
    theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[4];
  const goalDividerStyle: React.CSSProperties = {
    borderLeft: `1px solid ${goalDividerColor}`,
  };
  const cfg = LEVEL_CONFIG[level];

  const filtered = rows;

  // ChatGPT, Organic and Direct are single aggregate rows, so they are never hidden.
  const hideKeyOf = (row: NetworkRow) =>
    (row.source === 'chatgpt' || row.source === 'organic' || row.source === 'direct' ? '' : campaignHideKey(row, level));
  const hiddenKeySet = new Set(hiddenCampaignKeys);
  const isHiddenRow = (row: NetworkRow) => hiddenKeySet.has(hideKeyOf(row));
  // Rows unhidden since the last fetch stay on this page until the next fetch places them.
  // Organic and Direct (no ad behind them) always close the table, Organic first, whatever the sort.
  const isUnpaidRow = (row: NetworkRow) => row.source === 'organic' || row.source === 'direct';
  const visibleRows = (() => {
    const onPage = new Set(filtered.map((r) => `${r.source}:${r.id}`));
    const shown = [
      ...filtered.filter((r) => !isHiddenRow(r)),
      ...serverHiddenRows.filter((r) => !onPage.has(`${r.source}:${r.id}`) && !isHiddenRow(r)),
    ];
    return [
      ...shown.filter((r) => !isUnpaidRow(r)),
      ...shown.filter((r) => r.source === 'organic'),
      ...shown.filter((r) => r.source === 'direct'),
    ];
  })();
  // Hidden rows from every page (server) plus rows hidden on this page since the last fetch.
  const hiddenRows = (() => {
    const seen = new Set<string>();
    return [...serverHiddenRows, ...filtered].filter((r) => {
      const rowKey = `${r.source}:${r.id}`;
      if (seen.has(rowKey) || !isHiddenRow(r)) { return false; }
      seen.add(rowKey);
      return true;
    });
  })();
  const [hiddenOpen, setHiddenOpen] = useState(false);

  // A hidden campaign's rows drop out of the selection so drill-downs never carry them.
  useEffect(() => {
    const hiddenIds = hiddenRows.map((r) => r.id);
    if (hiddenIds.length === 0) { return; }
    setSelectedIds((prev) => {
      const next = prev.filter((id) => !hiddenIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenCampaignKeys, rows]);

  const renderHideToggle = (row: NetworkRow) => {
    const key = hideKeyOf(row);
    if (key === '') { return null; }
    return <CampaignHideToggle hidden={hiddenKeySet.has(key)} onToggle={() => onToggleHiddenCampaign(key)} />;
  };

  const hasCheckboxColumn = level !== 'ad';
  const showPlacement = rows.some((r) => r.source === 'google') || serverHiddenRows.some((r) => r.source === 'google');
  const trafficColCount = orderedTrafficMetrics.length;
  const totalColumns =
    (hasCheckboxColumn ? 1 : 0) +
    1 /* source */ +
    cfg.extraColumnLabels.length +
    (showPlacement ? 1 : 0) +
    trafficColCount +
    goalColCount;

  const prominentSx = { width: 220, fontWeight: 700, fontSize: 14, textAlign: 'left' as const };
  const dimSx = (t: any) => ({
    width: 200,
    fontWeight: 400,
    fontSize: 12,
    textAlign: 'left' as const,
    color: t.colorScheme === 'dark' ? t.colors.dark[2] : t.colors.gray[6],
  });

  const renderDrillName = (name: string, row: NetworkRow) => {
    if (!onDrillDown || level === 'ad') {
      return (
        <Text weight={prominentSx.fontWeight} sx={{ fontSize: prominentSx.fontSize }}>
          {name}
        </Text>
      );
    }
    return (
      <UnstyledButton
        onClick={() => onDrillDown(row)}
        sx={{ ...prominentSx, '&:hover': { textDecoration: 'none' } }}
      >
        {name}
      </UnstyledButton>
    );
  };

  // Width caps + overflowWrap so underscore-heavy names don't break layout.
  const HIER_CELL_STYLES = {
    campaign: { minWidth: 220, maxWidth: 280, overflowWrap: 'anywhere' as const, whiteSpace: 'normal' as const, verticalAlign: 'top' as const },
    adset:    { minWidth: 220, maxWidth: 280, overflowWrap: 'anywhere' as const, whiteSpace: 'normal' as const, verticalAlign: 'top' as const },
    ad:       { minWidth: 240, maxWidth: 320, overflowWrap: 'anywhere' as const, whiteSpace: 'normal' as const, verticalAlign: 'top' as const },
  };
  const HIER_TH_STYLES = {
    campaign: { minWidth: 220, width: 240 } as React.CSSProperties,
    adset:    { minWidth: 220, width: 240 } as React.CSSProperties,
    ad:       { minWidth: 240, width: 280 } as React.CSSProperties,
  };
  const HEADER_LABEL_TO_KEY: Record<string, keyof typeof HIER_TH_STYLES> = {
    'Campaign': 'campaign',
    'Ad Set':   'adset',
    'Ad':       'ad',
  };

  // Identity columns stay pinned while the metric columns scroll, as in the campaign report.
  // Widths are fixed so each column's left offset is the sum of the ones before it.
  const stickyKeys: string[] = [
    ...(hasCheckboxColumn ? ['checkbox'] : []),
    'source',
    ...cfg.extraColumnLabels.map((label) => HEADER_LABEL_TO_KEY[label] as string),
  ];
  const stickyLeft: Record<string, number> = {};
  stickyKeys.reduce((left, key) => {
    stickyLeft[key] = left;
    return left + STICKY_WIDTHS[key];
  }, 0);
  const hierKeys: string[] = cfg.extraColumnLabels.map((label) => HEADER_LABEL_TO_KEY[label] as string);
  // Same surface as the Paper behind the table, so a pinned column is not visibly different;
  // it only has to be opaque so scrolling columns cannot show through it.
  const stickyBg = theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0];
  // Sub-rows carry a tint of their own; pinned cells need an opaque equivalent.
  const stickySubBg = theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1];
  // Header sits above body cells so a pinned header cell covers the rows scrolling under it.
  const stickyCell = (key: string, zIndex: number, extra?: React.CSSProperties): React.CSSProperties => ({
    position: 'sticky',
    left: stickyLeft[key],
    zIndex,
    background: stickyBg,
    width: STICKY_WIDTHS[key],
    minWidth: STICKY_WIDTHS[key],
    maxWidth: STICKY_WIDTHS[key],
    overflow: 'hidden',
    ...extra,
  });

  /* Gate for the per-ad Keyword View only. That one reads the {keyword} ValueTrack Google writes
     into the landing URL, and Google fills it on Search clicks alone, so on any other channel the
     drawer can only ever show one unnamed row. The ad group's Keyword View is not gated: it asks
     Google directly, and Display ad groups do carry contextual keywords of their own.
     A blank placement means the channel was never synced, so the button stays rather than
     disappearing on data we cannot classify. */
  const isSearchPlacement = (placement?: string) => {
    const channel = (placement ?? '').trim().toUpperCase();
    return channel === '' || channel === 'SEARCH';
  };

  /* Keyword View opens on a Google ad group, so it belongs on the Ad Set column at both the
     Ad Set level (the row's own id) and the Ad level (its parent ad group). */
  const keywordViewButton = (row: NetworkRow, adgroupId: string, adgroupName: string) => {
    if (row.source !== 'google' || !adgroupId || !velocityContext) { return null; }
    return (
      <button
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setKeywordParams({
            visiopt_campaign_id: velocityContext.visiopt_campaign_id,
            adset_id: adgroupId,
            adset_name: adgroupName,
            report_time: velocityContext.report_time,
            ad_account_id: velocityContext.ad_account_id ?? [],
          });
          setKeywordOpen(true);
        }}
        style={{ ...keywordViewButtonStyle, flex: '0 0 auto' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = keywordViewHoverInBg; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = keywordViewHoverOutBg; }}
        onMouseDown={(e) => { e.currentTarget.style.backgroundColor = keywordViewActiveBg; }}
        onMouseUp={(e) => { e.currentTarget.style.backgroundColor = keywordViewHoverInBg; }}
      >
        Keyword View
      </button>
    );
  };

  /* Keyword View for one individual ad. Sibling of keywordViewButton above, which opens the ad
     group's Google keywords - this one opens Visiopt's per-keyword results for the ad itself. */
  const adKeywordViewButton = (row: NetworkRow, adName: string) => {
    if (row.source !== 'google' || !row.id || !velocityContext || !isSearchPlacement(row.placement)) { return null; }
    return (
      <button
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setAdKeywordParams({
            visiopt_campaign_id: velocityContext.visiopt_campaign_id,
            filter_visiopt_campaign_ids: velocityContext.visiopt_campaign_id,
            ad_id: row.id,
            ad_name: adName,
            attribution_model: velocityContext.attribution_model,
            report_time: velocityContext.report_time,
          });
          setAdKeywordOpen(true);
        }}
        style={{ ...keywordViewButtonStyle, flex: '0 0 auto' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = keywordViewHoverInBg; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = keywordViewHoverOutBg; }}
        onMouseDown={(e) => { e.currentTarget.style.backgroundColor = keywordViewActiveBg; }}
        onMouseUp={(e) => { e.currentTarget.style.backgroundColor = keywordViewHoverInBg; }}
      >
        Keyword View
      </button>
    );
  };

  // Parent-first hierarchy: drill-name cell is last; preceding cells are dimmed parents.
  const renderHierarchyCells = (row: NetworkRow) => {
    if (level === 'campaign') {
      // ChatGPT, Organic and Direct are single aggregate rows (no drilldown) — show a muted placeholder instead of a drillable name.
      if (row.source === 'chatgpt' || row.source === 'organic' || row.source === 'direct') {
        return <td style={{ ...HIER_CELL_STYLES.campaign, ...stickyCell('campaign', 2), verticalAlign: 'middle', textAlign: 'center' }}>-</td>;
      }
      return (
        <td style={{ ...HIER_CELL_STYLES.campaign, ...stickyCell('campaign', 2) }}>
          <Flex align="center" columnGap={8} sx={{ width: '100%' }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0, '& > *': { maxWidth: '100%' } }}>{renderDrillName(row.campaign ?? row.entity_name, row)}</Box>
            {renderHideToggle(row)}
          </Flex>
        </td>
      );
    }
    if (level === 'adset') {
      return (
        <>
          <td style={{ ...HIER_CELL_STYLES.campaign, ...stickyCell('campaign', 2) }}>
            <Flex align="center" columnGap={8} sx={{ width: '100%' }}>
              <Text sx={(t) => ({ ...dimSx(t), flex: '1 1 auto', minWidth: 0 })}>{row.campaign ?? '-'}</Text>
              {renderHideToggle(row)}
            </Flex>
          </td>
          <td style={{ ...HIER_CELL_STYLES.adset, ...stickyCell('adset', 2) }}>
            <Flex align="flex-start" wrap="wrap" columnGap={8} rowGap={4} sx={{ width: '100%' }}>
              <Box sx={{ flex: '0 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
                {renderDrillName(row.adset ?? row.entity_name, row)}
              </Box>
              {keywordViewButton(row, row.id, (row.adset ?? row.entity_name) ?? '')}
            </Flex>
          </td>
        </>
      );
    }
    const adName = row.ad ?? row.entity_name;
    const previewable = !!row.id && AD_PREVIEW_SOURCES.has(row.source);
    return (
      <>
        <td style={{ ...HIER_CELL_STYLES.campaign, ...stickyCell('campaign', 2) }}>
          <Flex align="center" columnGap={8} sx={{ width: '100%' }}>
            <Text sx={(t) => ({ ...dimSx(t), flex: '1 1 auto', minWidth: 0 })}>{row.campaign ?? '-'}</Text>
            {renderHideToggle(row)}
          </Flex>
        </td>
        <td style={{ ...HIER_CELL_STYLES.adset, ...stickyCell('adset', 2) }}>
          <Flex align="flex-start" wrap="wrap" columnGap={8} rowGap={4} sx={{ width: '100%' }}>
            <Box sx={{ flex: '0 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
              <Text sx={dimSx}>{row.adset ?? '-'}</Text>
            </Box>
            {keywordViewButton(row, row.parent_adset_id ?? '', row.adset ?? '')}
          </Flex>
        </td>
        <td style={{ ...HIER_CELL_STYLES.ad, ...stickyCell('ad', 2) }}>
          <Flex align="flex-start" wrap="wrap" columnGap={8} rowGap={4} sx={{ width: '100%' }}>
            <Box sx={{ flex: '0 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
          {previewable ? (
            <UnstyledButton
              onClick={() => {
                setAdPreview({
                  platform: row.source as 'facebook' | 'google',
                  adName: adName ?? '',
                  adId: row.id,
                });
                setAdPreviewOpen(true);
              }}
              sx={{
                ...prominentSx,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {adName}
            </UnstyledButton>
          ) : (
            <Text weight={prominentSx.fontWeight} sx={{ fontSize: prominentSx.fontSize }}>
              {adName}
            </Text>
          )}
            </Box>
            {adKeywordViewButton(row, adName ?? '')}
          </Flex>
        </td>
      </>
    );
  };

  /* Select-all, as on the campaign report. ChatGPT, Organic and Direct are aggregate rows with no ad
     sets or ads, so they are never picked - the same rule the row checkboxes follow. */
  const selectableRowIds = visibleRows
    .filter((r) => r.source !== 'chatgpt' && r.source !== 'organic' && r.source !== 'direct')
    .map((r) => r.id);
  const allRowsSelected = selectableRowIds.length > 0
    && selectableRowIds.every((id) => selectedIds.includes(id));
  const someRowsSelected = !allRowsSelected && selectableRowIds.some((id) => selectedIds.includes(id));

  const selectionCount = selectedIds.length;
  const nextLevelLabel = level === 'campaign' ? 'ad sets' : level === 'adset' ? 'ads' : null;
  const helperLine = (() => {
    if (selectionCount > 0 && nextLevelLabel) {
      const noun = level === 'campaign' ? 'campaign' : 'ad set';
      return `${selectionCount} ${noun}${selectionCount > 1 ? 's' : ''} selected · click a name to drill into ${
        selectionCount > 1 ? 'their' : 'its'
      } ${nextLevelLabel}, or the route icon to see customer journeys.`;
    }
    return cfg.helper;
  })();

  const showTable = eligible && (visibleRows.length > 0 || hiddenRows.length > 0);
  const resolvedEmptyMessage = emptyMessage || cfg.defaultEmptyMessage;

  /* Same Day and Full Impact credit no single click, so the assist cells hold no numbers.
     Say why once, above the table, rather than repeating it in every cell. */
  /*
   * Assisted Conversion / Influenced Revenue: which goals ask the backend, and which display.
   *
   * Checking Assisted Conversion is the trigger - there is no per-row action - and it always
   * refreshes that goal, even when the goal is already on screen because its Influenced Revenue
   * column is showing. Influenced Revenue itself never asks: it shows what that same answer
   * carried, which is why it is in the visible list but not the fetch list. One request still
   * fills a whole column at once: the endpoint costs one query per (source x goal) however many
   * rows there are, where fetching per cell would be an N+1.
   *
   * It never blocks the table. The grid has already rendered by the time this runs; the assist
   * cells show a skeleton until the answer lands.
   */
  /* eslint-disable react-hooks/exhaustive-deps */
  /* goalMetricsOrdered is recreated every render, so it cannot be a dependency; the inputs that
     actually change its result are listed instead. */
  const assistFetchGoalKeys = useMemo(
    () => activeGoals
      .filter((g) => goalMetricsOrdered(g.name).includes(ASSISTED_METRIC))
      .map((g) => g.key),
    [activeGoals, trafficFunnelMetricsByGoal, level]
  );
  const assistVisibleGoalKeys = useMemo(
    () => activeGoals
      .filter((g) => goalMetricsOrdered(g.name).some(isAssistMetric))
      .map((g) => g.key),
    [activeGoals, trafficFunnelMetricsByGoal, level]
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  const assistLoaderEnabled = level === 'ad' && assistsSupported && !assistsLoaded && !!assistContext;
  const assistLoader = useAssistLoader({
    enabled: assistLoaderEnabled,
    context: assistContext,
    level,
    fetchGoalKeys: assistFetchGoalKeys,
    visibleGoalKeys: assistVisibleGoalKeys,
    rows,
    fetcher: fetchAssistCells,
  });
  /* The Assisted drawer is asked the same question as the cell: same settings, same window. */
  const assistJourneyContext = assistContext ?? velocityContext;

  const assistColumnsShown = activeGoals.some((g) =>
    goalMetricsOrdered(g.name).some(isAssistMetric)
  );
  const showAssistNotice = assistColumnsShown && !assistsSupported;

  const renderRow = (row: NetworkRow) => {
    /* Customer Type has exactly two segments, so its rows are drawn from the
     labels alone - the layout is reviewable before any figures exist, and
     these same rows fill in once the report supplies customer_type_breakdown. */
    const CT_SEGMENTS = ['First-time Customers', 'Returning Customers'];
    const ctSupplied = Array.isArray(row.customer_type_breakdown) ? row.customer_type_breakdown : [];
    const subRows = (row.device_breakdown && row.device_breakdown.length > 0)
      ? row.device_breakdown
      : (row.hour_breakdown && row.hour_breakdown.length > 0)
      ? row.hour_breakdown
      : (breakdownType === 'cust_type')
      ? CT_SEGMENTS.map((name) => {
        const hit = ctSupplied.find((x: any) => x && x.customer_type_name === name);
        /* Typed loosely on purpose: a placeholder row carries only its label until the
           report supplies figures for it. */
        return (hit ? hit : { customer_type_name: name }) as any;
      })
      : [];
    return (
      <React.Fragment key={row.id}>
        <tr>
          {hasCheckboxColumn && (
            <td style={stickyCell('checkbox', 2)}>
              {row.source !== 'chatgpt' && row.source !== 'organic' && row.source !== 'direct' && (
                <Checkbox
                  checked={selectedIds.includes(row.id)}
                  // Hidden rows are never selected, so a drill-down cannot carry them.
                  disabled={isHiddenRow(row)}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setSelectedIds((prev) =>
                      checked ? [...prev, row.id] : prev.filter((id) => id !== row.id)
                    );
                  }}
                />
              )}
            </td>
          )}
          <td style={stickyCell('source', 2)}>
            <Badge color={sourceChipColor(row.source_label)} variant={row.source === 'chatgpt' ? 'outline' : 'light'} radius="sm">
              {row.source_label}
            </Badge>
          </td>
          {renderHierarchyCells(row)}
          {showPlacement && (
            <td>
              <Text sx={dimSx}>{row.placement ? row.placement : '-'}</Text>
            </td>
          )}
          {orderedTrafficMetrics.map((m) =>
            GRADE_COLUMNS.includes(m)
              ? <GradeCellTd key={m} grade={row.grade} reason={row.grade_reason} column={m} />
              : <td key={m}>{cellBody(trafficCell(row, m))}</td>
          )}
          {activeGoals.flatMap((goal) =>
            goalMetricsOrdered(goal.name).map((m, i) => {
              const goalMetric = row.goals?.[goal.key];
              const hasConversions =
                m === 'Conversions'
                && !!velocityContext
                && (goalMetric?.conversions ?? 0) > 0;
              /* Assisted opens the same drawer the Conversions cell does,
                 asking it for the assisted conversions instead of the credited
                 ones. Nothing to open when the ad assisted none. */
              /* Figures from useAssistLoader override the row's own, which read
                 "not measured" while assists load on demand. An ad the endpoint
                 answered for but that assisted nothing reads 0 / $0.00. */
              const fetchedAssist = isAssistMetric(m)
                ? assistLoader.value(row.id, goal.key)
                : undefined;
              const effectiveMetric = fetchedAssist
                ? { ...(goalMetric as NetworkGoalMetric), ...fetchedAssist }
                : goalMetric;
              /* Waiting on that answer: a skeleton in this cell only, rather than
                 a zero that is not yet known to be one. */
              const assistPending = isAssistMetric(m)
                && assistLoaderEnabled
                && assistLoader.cellState(row.id, goal.key) === 'loading';
              /* Could not be computed this time - neither a real 0 / $0.00 nor
                 "not measurable", so it says so and offers to try again. */
              const assistFailed = isAssistMetric(m)
                && assistLoaderEnabled
                && assistLoader.cellState(row.id, goal.key) === 'failed';
              const showAssistJourney =
                m === ASSISTED_METRIC
                && !!assistJourneyContext
                && canOpenAssistJourney(effectiveMetric?.assisted, assistsSupported);
              return (
                <td
                  key={`${goal.key}-${m}`}
                  style={i === 0 ? goalDividerStyle : undefined}
                >
                  {hasConversions ? (
                    <Flex align="center" gap={6}>
                      {/* The count is plain text; the route icon beside it opens the journeys. */}
                      <Text component="span" size="sm">{renderGoalCell(goalMetric, m)}</Text>
                      <JourneyIconButton
                        onClick={() => {
                          if (!velocityContext) { return; }
                          setJourneyParams({
                            report_id: velocityContext.report_id,
                            visiopt_campaign_id: velocityContext.visiopt_campaign_id,
                            attribution_model: velocityContext.attribution_model,
                            report_time: velocityContext.report_time,
                            attribute_window_type: velocityContext.attribute_window_type,
                            lookback_days: velocityContext.lookback_days,
                            traffic_source: row.source,
                            goal_key: goal.key,
                            level: level,
                            ad_entity_id: row.id,
                            parent_campaign_id: row.parent_campaign_id ?? '',
                            parent_adset_id: row.parent_adset_id ?? '',
                          });
                          setJourneyEntityLabel((row.ad ?? row.adset ?? row.campaign ?? row.entity_name) ?? '');
                          setJourneyOpen(true);
                        }}
                      />
                      {/* Velocity is per ad, so Organic / Direct rows have none. */}
                      {row.source !== 'organic' && row.source !== 'direct' && (
                      <Tooltip label="View velocity report" withArrow withinPortal>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="indigo"
                          aria-label="View velocity report"
                          onClick={() => {
                            if (!velocityContext) { return; }
                            setVelocityParams({
                              report_id: velocityContext.report_id,
                              visiopt_campaign_id: velocityContext.visiopt_campaign_id,
                              attribution_model: velocityContext.attribution_model,
                              report_time: velocityContext.report_time,
                              attribute_window_type: velocityContext.attribute_window_type,
                              lookback_days: velocityContext.lookback_days,
                              traffic_source: row.source,
                              goal_key: goal.key,
                              level: level,
                              ad_entity_id: row.id,
                              parent_campaign_id: row.parent_campaign_id ?? '',
                              parent_adset_id: row.parent_adset_id ?? '',
                            });
                            setVelocityDisplayName((row.ad ?? row.adset ?? row.campaign ?? row.entity_name) ?? '');
                            setVelocityOpen(true);
                          }}
                        >
                          <IconClock size="0.95rem" />
                        </ActionIcon>
                      </Tooltip>
                      )}
                    </Flex>
                  ) : assistPending ? (
                    /* Lightweight placeholder: the grid is already usable and
                       this is the only cell still waiting. */
                    <Skeleton height={12} width={44} radius="sm" />
                  ) : assistFailed ? (
                    /* One click retries every failed cell on screen in a single
                       request, never one request per cell. */
                    <Tooltip label="Couldn't load this figure. Click to try again." withArrow>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="orange"
                        aria-label="Try loading again"
                        onClick={assistLoader.retry}
                      >
                        <IconRefresh size="0.9rem" />
                      </ActionIcon>
                    </Tooltip>
                  ) : showAssistJourney ? (
                    <Flex align="center" gap={6}>
                      {/* The count is plain text; the route icon beside it opens the assisted journeys,
                          asked the same question as the cell: same settings, same window. */}
                      <Text component="span" size="sm">{cellBody(goalCell(effectiveMetric, m))}</Text>
                      <JourneyIconButton
                        onClick={() => {
                          if (!assistJourneyContext) { return; }
                          setJourneyParams({
                            report_id: assistJourneyContext.report_id,
                            visiopt_campaign_id: assistJourneyContext.visiopt_campaign_id,
                            attribution_model: assistJourneyContext.attribution_model,
                            report_time: assistJourneyContext.report_time,
                            attribute_window_type: assistJourneyContext.attribute_window_type,
                            lookback_days: assistJourneyContext.lookback_days,
                            traffic_source: row.source,
                            goal_key: goal.key,
                            level: level,
                            ad_entity_id: row.id,
                            parent_campaign_id: row.parent_campaign_id ?? '',
                            parent_adset_id: row.parent_adset_id ?? '',
                            journey_metric: 'assisted',
                          });
                          setJourneyEntityLabel((row.ad ?? row.adset ?? row.campaign ?? row.entity_name) ?? '');
                          setJourneyOpen(true);
                        }}
                      />
                    </Flex>
                  ) : (
                    renderGoalCell(effectiveMetric, m)
                  )}
                </td>
              );
            })
          )}
        </tr>
        {subRows.map((sub, si) => {
          const subLabel = sub.device_name ?? sub.hour_label ?? sub.customer_type_name ?? '';
        /* Customer Type only applies to a webhook goal with Count as Purchase
           enabled. Every other column reads a muted "-" rather than a blank,
           so an empty cell is never mistaken for a zero. Eligibility is
           supplied by the report; nothing is decided here. */
        const isCt = breakdownType === 'cust_type';
        const ctEligible = (colKey: string) => {
          const flags = (row as any)?.customer_type_eligible;
          if (!flags || typeof flags !== 'object') { return false; }
          const hit = Object.keys(flags).find((k) => colKey.includes(k) || k.includes(colKey));
          return hit ? Number(flags[hit]) === 1 : false;
        };
        const ctNa = (
          <span style={{ color: '#adb5bd', fontStyle: 'italic' }} title="Customer Type applies only to webhook goals with Count as Purchase enabled">-</span>
        );
          const hierCount = cfg.extraColumnLabels.length;
          const subBg = { background: 'rgba(0,0,0,0.025)', fontStyle: 'italic' as const };
          return (
            <tr key={`${row.id}-sub-${si}`}>
              {hasCheckboxColumn && (
                <td style={{ ...subBg, ...stickyCell('checkbox', 2), background: stickySubBg }}></td>
              )}
              <td style={{ ...subBg, ...stickyCell('source', 2), background: stickySubBg }}></td>
              {Array.from({ length: Math.max(0, hierCount - 1) }).map((_, hi) => (
                <td
                  key={`pad-${hi}`}
                  style={{ ...subBg, ...stickyCell(hierKeys[hi], 2), background: stickySubBg }}
                ></td>
              ))}
              <td
                style={{
                  ...subBg,
                  ...stickyCell(hierKeys[hierCount - 1], 2),
                  background: stickySubBg,
                  paddingLeft: 32,
                  color: '#666',
                }}
              >↳ {subLabel}</td>
              {showPlacement && (
                <td style={subBg}></td>
              )}
              {orderedTrafficMetrics.map((m) =>
                GRADE_COLUMNS.includes(m)
                  ? <GradeCellTd key={m} grade={sub.grade} reason={sub.grade_reason} column={m} tdStyle={subBg} />
                  : <td key={m} style={subBg}>{isCt ? ctNa : (ESTIMATED_BREAKDOWN_METRICS.includes(m) ? estimatedCellBody(trafficCell(sub as any, m), m) : cellBody(trafficCell(sub as any, m)))}</td>

              )}
              {activeGoals.flatMap((goal) =>
                goalMetricsOrdered(goal.name).map((m, i) => (
                  <td
                    key={`${goal.key}-${m}`}
                    style={{ ...subBg, ...(i === 0 ? goalDividerStyle : {}) }}
                  >
                    {isCt && !ctEligible(goal.key)
                      ? ctNa
                      : (!isCt && ESTIMATED_BREAKDOWN_METRICS.includes(m))
                        ? estimatedCellBody(goalCell(sub.goals?.[goal.key], m))
                        : renderGoalCell(sub.goals?.[goal.key], m)}
                  </td>
                ))
              )}
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  const leafHeaders: Array<
    | { kind: 'traffic'; metric: string }
    | { kind: 'goal'; goalKey: string; goalName: string; scoreLabel?: string; metric: string; isFirstInGoal: boolean }
  > = [];
  for (const m of orderedTrafficMetrics) {
    leafHeaders.push({ kind: 'traffic', metric: m });
  }
  for (const goal of activeGoals) {
    const cols = goalMetricsOrdered(goal.name);
    cols.forEach((m, i) => {
      leafHeaders.push({
        kind: 'goal',
        goalKey: goal.key,
        goalName: goal.name,
        scoreLabel: goal.score_label,
        metric: m,
        isFirstInGoal: i === 0,
      });
    });
  }

  return (
    <Tabs.Panel value={cfg.tabValue} pt="lg">
      <Flex justify="space-between" align="center" mb="xs" wrap="wrap" gap="sm">
        <Flex align="center" gap={4}>
          <Text size="xs" color="dimmed">
            {helperLine}
            {level === 'campaign' && (
              <>
                <br />
                Shows visits matched to an ad campaign. Paid visits without a Visiopt ad tag are counted in Sources but not listed here.
              </>
            )}
          </Text>
          <SectionHelp section="ad" title="Campaign, Ad Set and Ad metrics" breakdownType={breakdownType} />
        </Flex>
        <Flex align="center" gap="xs">
          <TextInput
            size="sm"
            placeholder={cfg.searchPlaceholder}
            icon={<IconSearch size="0.9rem" />}
            value={search}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            w={260}
          />
          <ActionIcon
            variant="default"
            size="md"
            title={cfg.searchTitle}
            onClick={() => openSettings(cfg.settingsContext, cfg.settingsScope)}
          >
            <IconSettings size="1rem" />
          </ActionIcon>
        </Flex>
      </Flex>

      {drillFilterLabel && onClearDrillFilter && (
        <Flex align="center" gap="xs" mb="xs" wrap="wrap">
          <Badge
            size="lg"
            radius="sm"
            variant="outline"
            color="teal"
            sx={(t) => ({
              textTransform: 'none',
              paddingRight: 6,
              borderColor: t.colors.teal[t.colorScheme === 'dark' ? 6 : 4],
            })}
            rightSection={
              <ActionIcon size="xs" variant="transparent" onClick={onClearDrillFilter}>
                <IconX size="0.75rem" />
              </ActionIcon>
            }
          >
            <Text component="span" size="sm">
              <Text component="span" color="dimmed" size="sm">
                Filtered by:{' '}
              </Text>
              <Text component="span" weight={600} size="sm">
                {drillFilterLabel}
              </Text>
            </Text>
          </Badge>
          <Button size="xs" variant="subtle" color="gray" onClick={onClearDrillFilter}>
            Clear filter
          </Button>
        </Flex>
      )}

      {showAssistNotice && (
        <Flex align="center" gap={6} mb="xs" wrap="nowrap">
          <IconInfoCircle size="0.9rem" style={{ flex: '0 0 auto', opacity: 0.7 }} />
          <Text size="xs" color="dimmed">
            {assistsMessage}
          </Text>
        </Flex>
      )}

      {!showTable ? (
        <Paper
          withBorder
          radius="md"
          p="xl"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            textAlign: 'center',
          })}
        >
          <Text color="dimmed" size="sm">
            {loading ? 'Loading…' : resolvedEmptyMessage}
          </Text>
        </Paper>
      ) : (
        <Paper
          withBorder
          radius="md"
          p="sm"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
          })}
        >
          <ScrollArea type="auto" offsetScrollbars>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Table verticalSpacing="sm" horizontalSpacing="md" sx={{ minWidth: cfg.minWidth, ...CAMPAIGN_HIDE_SX }}>
                <thead>
                  <tr>
                    {hasCheckboxColumn && (
                      <th rowSpan={2} style={stickyCell('checkbox', 4)}>
                        <Tooltip
                          withArrow
                          withinPortal
                          label={allRowsSelected
                            ? `Clear all ${cfg.tabValue === 'campaign' ? 'campaigns' : 'ad sets'} on this page`
                            : `Select all ${cfg.tabValue === 'campaign' ? 'campaigns' : 'ad sets'} on this page`}
                        >
                          <Checkbox
                            checked={allRowsSelected}
                            indeterminate={someRowsSelected}
                            disabled={selectableRowIds.length === 0}
                            aria-label="Select all rows"
                            onChange={(e) => {
                              const checked = e.currentTarget.checked;
                              setSelectedIds(() => (checked ? selectableRowIds : []));
                            }}
                          />
                        </Tooltip>
                      </th>
                    )}
                    <th rowSpan={2} style={stickyCell('source', 4)}>
                      <UnstyledButton onClick={() => handleSortToggle('source')} sx={{ width: '100%' }}>
                        <Flex align="center" gap={4}>
                          <Text size="xs" color="dimmed" transform="uppercase" weight={600}>Source</Text>
                          {sortArrow('source')}
                        </Flex>
                      </UnstyledButton>
                    </th>
                    {cfg.extraColumnLabels.map((label) => {
                      const widthKey = HEADER_LABEL_TO_KEY[label];
                      const thStyle = widthKey ? HIER_TH_STYLES[widthKey] : undefined;
                      const sortField = HEADER_LABEL_TO_FIELD[label] ?? label.toLowerCase();
                      return (
                        <th key={label} rowSpan={2} style={{ ...thStyle, ...(widthKey ? stickyCell(widthKey, 4) : {}) }}>
                          <UnstyledButton onClick={() => handleSortToggle(sortField)} sx={{ width: '100%' }}>
                            <Flex align="center" gap={4}>
                              <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                                {label}
                              </Text>
                              {sortArrow(sortField)}
                            </Flex>
                          </UnstyledButton>
                        </th>
                      );
                    })}
                    {showPlacement && (
                      <th rowSpan={2}>
                        <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                          Placement
                        </Text>
                      </th>
                    )}
                    {trafficColCount > 0 && (
                      <th colSpan={trafficColCount}>
                        <Text
                          size="xs"
                          color="dimmed"
                          transform="uppercase"
                          weight={700}
                          align="center"
                        >
                          Traffic
                        </Text>
                      </th>
                    )}
                    {activeGoals.map((goal) => (
                      <th
                        key={goal.key}
                        colSpan={goalMetricsOrdered(goal.name).length}
                        style={goalDividerStyle}
                      >
                        <Text
                          size="xs"
                          color={goal.is_primary ? 'teal' : 'dimmed'}
                          transform="uppercase"
                          weight={700}
                          align="center"
                        >
                          {goal.name}
                        </Text>
                      </th>
                    ))}
                  </tr>
                  <Droppable droppableId="leaf-headers" direction="horizontal" type="leaf-col">
                    {(dropProvided) => (
                      <tr
                        ref={dropProvided.innerRef as unknown as React.Ref<HTMLTableRowElement>}
                        {...dropProvided.droppableProps}
                      >
                        {leafHeaders.map((h, i) => {
                          const draggableId =
                            h.kind === 'traffic'
                              ? `traffic:${h.metric}`
                              : `goal:${h.goalKey}:${h.metric}`;
                          const baseStyle: React.CSSProperties =
                            h.kind === 'goal' && h.isFirstInGoal ? goalDividerStyle : {};
                          const metricToken = h.kind === 'traffic'
                            ? (TRAFFIC_METRIC_SORT_FIELD[h.metric] ?? '')
                            : (GOAL_METRIC_SORT_FIELD[h.metric] ?? '');
                          const sortField = h.kind === 'traffic'
                            ? metricToken
                            : (metricToken ? `goal:${h.goalKey}:${metricToken}` : '');
                          return (
                            <Draggable key={draggableId} draggableId={draggableId} index={i}>
                              {(dragProvided, snapshot) => (
                                <th
                                  ref={dragProvided.innerRef as unknown as React.Ref<HTMLTableCellElement>}
                                  {...dragProvided.draggableProps}
                                  style={{
                                    ...baseStyle,
                                    ...(dragProvided.draggableProps.style as React.CSSProperties),
                                    background: snapshot.isDragging
                                      ? theme.colorScheme === 'dark'
                                        ? theme.colors.dark[5]
                                        : theme.colors.gray[1]
                                      : undefined,
                                  }}
                                >
                                  <Flex align="center" gap={4} sx={{ width: '100%' }}>
                                    <span {...dragProvided.dragHandleProps} style={{ display: 'inline-flex', cursor: 'grab', flex: '0 0 auto' }}>
                                      <IconGripVertical size="0.75rem" opacity={0.45} />
                                    </span>
                                    <UnstyledButton
                                      onClick={() => sortField && handleSortToggle(sortField)}
                                      sx={{ flex: 1 }}
                                      disabled={!sortField}
                                    >
                                      <Flex align="center" gap={4}>
                                        <Text
                                          size="xs"
                                          color="dimmed"
                                          transform="uppercase"
                                          weight={600}
                                          sx={{ whiteSpace: 'nowrap' }}
                                        >
                                          {metricDisplayLabel(h.metric, h.kind === 'goal' ? h.scoreLabel : undefined)}
                                        </Text>
                                        {sortField ? sortArrow(sortField) : null}
                                      </Flex>
                                    </UnstyledButton>
                                    {/* Outside the sort button so reading the help text does not resort the table. */}
                                    {isAssistMetric(h.metric) && (
                                      <AdminHelpTip
                                        helpId={h.metric === ASSISTED_METRIC ? REPORT_HELP_IDS.assistedConversion : REPORT_HELP_IDS.influencedRevenue}
                                        label={metricDisplayLabel(h.metric)}
                                      />
                                    )}
                                  </Flex>
                                </th>
                              )}
                            </Draggable>
                          );
                        })}
                        {dropProvided.placeholder}
                      </tr>
                    )}
                  </Droppable>
                </thead>
                <tbody>
                  {visibleRows.map(renderRow)}
                  {hiddenRows.length > 0 && (
                    <HiddenRowsToggle
                      count={hiddenRows.length}
                      open={hiddenOpen}
                      onToggle={() => setHiddenOpen((o) => !o)}
                      colSpan={totalColumns}
                      borderColor={goalDividerColor}
                    />
                  )}
                  {hiddenOpen && hiddenRows.map(renderRow)}
                  {visibleRows.length === 0 && hiddenRows.length === 0 && (
                    <tr>
                      <td colSpan={totalColumns}>
                        <Text align="center" color="dimmed" size="sm" py="md">
                          {resolvedEmptyMessage}
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </DragDropContext>
          </ScrollArea>
          {totalRecords > 0 && (
            <Flex justify="space-between" align="center" mt="sm" wrap="wrap" gap="xs">
              <Text size="xs" color="dimmed">
                {`Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, totalRecords)} of ${totalRecords}`}
              </Text>
              {totalPages > 1 && (
                <Pagination.Root total={totalPages} value={page} onChange={onPageChange}>
                  <Group spacing={5} position="right">
                    <Pagination.First />
                    <Pagination.Previous />
                    <Pagination.Items />
                    <Pagination.Next />
                    <Pagination.Last />
                  </Group>
                </Pagination.Root>
              )}
            </Flex>
          )}
        </Paper>
      )}

      {level === 'ad' && (
        <AggregateAdKeywordDrawer
          opened={adKeywordOpen}
          onClose={() => setAdKeywordOpen(false)}
          params={adKeywordParams}
          onExport={onKeywordExport}
        />
      )}

      {/* The ad group's Keyword View button sits on the Ad Set column at both levels, so the
          drawer has to be mounted at both - at Ad level it opens on the row's parent ad group. */}
      {(level === 'adset' || level === 'ad') && (
        <AggregateKeywordDrawer
          opened={keywordOpen}
          onClose={() => setKeywordOpen(false)}
          params={keywordParams}
          onExport={onKeywordExport}
        />
      )}

      {level === 'ad' && (
        <AdPreviewDrawer
          opened={adPreviewOpen}
          onClose={() => setAdPreviewOpen(false)}
          platform={adPreview.platform}
          adName={adPreview.adName}
          adId={adPreview.adId}
        />
      )}

      {velocityContext && (
        <ReportVelocityDrawer
          report_id={velocityContext.report_id}
          showVelocityDrawer={velocityOpen}
          setShowVelocityDrawer={setVelocityOpen}
          campaignGoalId=""
          encodeCampaignName=""
          selectedCampaignName=""
          adsetName=""
          trafficSource=""
          campaign_id=""
          trafficLevel={level === 'ad' ? 'ads' : level}
          goalType=""
          combinedGoalType={false}
          selectedAttributionModal={velocityContext.attribution_label ?? velocityContext.attribution_model}
          displayCampaignName={velocityDisplayName}
          velocityMode="aggregate"
          aggregateVelocityParams={velocityParams}
        />
      )}

      {velocityContext && (
        <AggregateCustomerJourney
          opened={journeyOpen}
          onClose={() => setJourneyOpen(false)}
          params={journeyParams}
          entityLabel={journeyEntityLabel}
        />
      )}
    </Tabs.Panel>
  );
}
